import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  doc, 
  updateDoc, 
  deleteDoc,
  addDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ShieldCheck, 
  Clock, 
  Check, 
  X, 
  Users2, 
  Award, 
  MapPin, 
  FileText, 
  Binary, 
  Maximize2,
  AlertCircle,
  Edit2,
  Save,
  Search,
  Plus,
  Trash2
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';

// --- PREMIUM BLINKING SKELETON WIREFRAME ---
const SkeletonCard = () => (
  <div className="w-full bg-white border border-slate-200/80 rounded-2xl p-5 grid grid-cols-1 lg:grid-cols-12 gap-6 animate-pulse">
    <div className="lg:col-span-8 space-y-4">
      <div className="flex gap-2">
        <div className="h-5 w-16 bg-slate-200 rounded" />
        <div className="h-5 w-24 bg-slate-200 rounded" />
      </div>
      <div className="space-y-3">
        <div className="space-y-1">
          <div className="h-2 w-16 bg-slate-200 rounded" />
          <div className="h-4 w-1/3 bg-slate-200 rounded" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <div className="h-2 w-20 bg-slate-200 rounded" />
            <div className="h-3 w-32 bg-slate-200 rounded" />
          </div>
          <div className="space-y-1">
            <div className="h-2 w-20 bg-slate-200 rounded" />
            <div className="h-3 w-32 bg-slate-200 rounded" />
          </div>
        </div>
        <div className="space-y-1 pt-2">
          <div className="h-2 w-24 bg-slate-200 rounded" />
          <div className="h-3 w-5/6 bg-slate-200 rounded" />
        </div>
      </div>
    </div>
    <div className="lg:col-span-2 flex items-center">
      <div className="w-full aspect-4/3 lg:h-20 bg-slate-100 rounded-xl" />
    </div>
    <div className="lg:col-span-2 flex flex-col justify-end gap-2">
      <div className="h-[34px] bg-slate-100 rounded-xl w-full" />
      <div className="h-[30px] bg-slate-100 rounded-lg w-full" />
    </div>
  </div>
);

const AdminDashboard = () => {
  // --- Core States ---
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); 
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  
  // --- Form Inline Editing States ---
  const [editingId, setEditingId] = useState(null);
  const [editFields, setEditFields] = useState({});

  // --- Quick Manual Creator States ---
  const [showCreator, setShowCreator] = useState(false);
  const [newRecord, setNewRecord] = useState({
    student: '', achievement: '', destination: '', description: '', schoolCode: '', status: 'approved'
  });

  // --- Live Snapshot Listener Sync ---
  useEffect(() => {
    const q = query(collection(db, "achievements"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setSubmissions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    }, (err) => {
      console.error("Firestore sync error:", err);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // --- Database Updates ---
  const handleUpdateStatus = async (id, newStatus) => {
    setProcessingId(id);
    try {
      await updateDoc(doc(db, "achievements", id), { status: newStatus });
    } catch (err) {
      console.error("Status update error:", err);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeleteRecord = async (id) => {
    if (!window.confirm("Permanently purge this record from the system completely? This operation is irreversible.")) return;
    setProcessingId(id);
    try {
      await deleteDoc(doc(db, "achievements", id));
      if (editingId === id) setEditingId(null);
    } catch (err) {
      console.error("Deletion error:", err);
    } finally {
      setProcessingId(null);
    }
  };

  // --- Inline Field Editor Core ---
  const startEditing = (item) => {
    setEditingId(item.id);
    setEditFields({
      student: item.student,
      achievement: item.achievement,
      destination: item.destination,
      description: item.description,
      schoolCode: item.schoolCode
    });
  };

  const handleSaveInlineEdit = async (id) => {
    setProcessingId(id);
    try {
      await updateDoc(doc(db, "achievements", id), {
        student: editFields.student.trim(),
        achievement: editFields.achievement.trim(),
        destination: editFields.destination.trim(),
        description: editFields.description.trim(),
        schoolCode: editFields.schoolCode.trim()
      });
      setEditingId(null);
    } catch (err) {
      console.error("Inline save error:", err);
    } finally {
      setProcessingId(null);
    }
  };

  // --- Manual Record Injection Creator ---
  const handleCreateRecord = async (e) => {
    e.preventDefault();
    if (!newRecord.student || !newRecord.achievement || !newRecord.schoolCode) return;
    
    setProcessingId('manual-form');
    try {
      await addDoc(collection(db, "achievements"), {
        ...newRecord,
        imageUrl: "", 
        createdAt: serverTimestamp()
      });
      setNewRecord({ student: '', achievement: '', destination: '', description: '', schoolCode: '', status: 'approved' });
      setShowCreator(false);
    } catch (err) {
      console.error("Manual creation error:", err);
    } finally {
      setProcessingId(null);
    }
  };

  // --- Advanced Multi-Match Pipeline Filter ---
  const filteredSubmissions = submissions.filter(item => {
    const matchesFilter = filter === 'all' || item.status === filter;
    const queryLower = searchQuery.toLowerCase();
    const matchesSearch = 
      (item.student?.toLowerCase() || '').includes(queryLower) ||
      (item.achievement?.toLowerCase() || '').includes(queryLower) ||
      (item.destination?.toLowerCase() || '').includes(queryLower) ||
      (item.schoolCode?.toLowerCase() || '').includes(queryLower);
    
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="w-full bg-[#fafafa] text-slate-900 font-sans antialiased min-h-screen py-12 px-4 md:px-6">
      <Helmet>
        <title>Control Terminal | Verification Console</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="w-full max-w-6xl mx-auto space-y-6">
        
        {/* --- Top Layout System Bar --- */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-xs flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-left">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="text-slate-900" size={20} />
              <h1 className="text-lg font-bold tracking-tight uppercase text-slate-900">Verification Terminal</h1>
            </div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Autonomous Control Layer</p>
          </div>

          <button
            onClick={() => setShowCreator(!showCreator)}
            className="w-full md:w-auto px-5 h-[38px] bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-xs active:scale-[0.99]"
          >
            <Plus size={14} /> {showCreator ? "Cancel Entry" : "Forge Entry"}
          </button>
        </div>

        {/* --- Collapsible Direct Data Entry Engine --- */}
        <AnimatePresence>
          {showCreator && (
            <motion.div
              initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
              className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm overflow-hidden text-left"
            >
              <form onSubmit={handleCreateRecord} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2 space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Student Name(s)</label>
                  <input type="text" required placeholder="e.g. Alice Smith & Bob Jones" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs font-semibold outline-none focus:border-slate-900 bg-[#fafafa]" value={newRecord.student} onChange={e => setNewRecord({...newRecord, student: e.target.value})} />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Student Code</label>
                  <input type="text" required placeholder="7-digit code" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs font-semibold outline-none focus:border-slate-900 bg-[#fafafa]" value={newRecord.schoolCode} onChange={e => setNewRecord({...newRecord, schoolCode: e.target.value.replace(/\D/g, '')})} />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Achievement Title</label>
                  <input type="text" required placeholder="e.g. 1st Place National" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs font-semibold outline-none focus:border-slate-900 bg-[#fafafa]" value={newRecord.achievement} onChange={e => setNewRecord({...newRecord, achievement: e.target.value})} />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Hosting Arena</label>
                  <input type="text" required placeholder="e.g. Nile University" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs font-semibold outline-none focus:border-slate-900 bg-[#fafafa]" value={newRecord.destination} onChange={e => setNewRecord({...newRecord, destination: e.target.value})} />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Default Strategy Status</label>
                  <select className="w-full px-3 py-2 border border-slate-200 bg-[#fafafa] rounded-lg text-xs font-semibold outline-none focus:border-slate-900" value={newRecord.status} onChange={e => setNewRecord({...newRecord, status: e.target.value})}>
                    <option value="approved">Instant Approve / Active</option>
                    <option value="pending">Enforce Pending Queue</option>
                  </select>
                </div>
                <div className="md:col-span-3 space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Brief Matrix Parameters Description</label>
                  <textarea rows="2" placeholder="System descriptions or notes..." className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs font-semibold outline-none focus:border-slate-900 resize-none bg-[#fafafa]" value={newRecord.description} onChange={e => setNewRecord({...newRecord, description: e.target.value})} />
                </div>
                <div className="md:col-span-3 pt-2 flex justify-end">
                  <button 
                    type="submit" 
                    disabled={processingId === 'manual-form'}
                    className="px-6 h-[34px] bg-slate-900 disabled:bg-slate-700 text-white text-[11px] font-bold uppercase tracking-wider rounded-lg hover:bg-slate-800 transition-all cursor-pointer flex items-center justify-center gap-2"
                  >
                    {processingId === 'manual-form' ? (
                      <svg className="animate-spin h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    ) : "Inject Document"}
                  </button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        {/* --- Structural Filtering and Search Sub-Deck --- */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 w-full bg-white border border-slate-200/80 rounded-2xl p-4 shadow-2xs">
          
          {/* Universal Search Input */}
          <div className="relative w-full md:max-w-xs flex items-center">
            <Search className="absolute left-3.5 text-slate-400" size={14} />
            <input 
              type="text" 
              placeholder="Query matrix parameters..." 
              className="w-full pl-9 pr-4 py-2 border border-slate-200 bg-slate-50/50 focus:bg-white rounded-xl text-xs font-semibold outline-none focus:border-slate-900 transition-all text-slate-900 placeholder:text-slate-400"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Filter Option Buttons */}
          <div className="flex bg-slate-100 p-1 rounded-xl w-full md:w-auto border border-slate-200/40">
            {['pending', 'approved', 'rejected', 'all'].map((type) => (
              <button
                key={type} onClick={() => setFilter(type)}
                className={`flex-1 md:flex-none px-4 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all cursor-pointer ${
                  filter === type ? 'bg-white text-slate-900 border border-slate-200/30 shadow-2xs' : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* --- Main Data Output Area --- */}
        {loading ? (
          <div className="grid grid-cols-1 gap-4 w-full">
            {[...Array(3)].map((_, idx) => <SkeletonCard key={idx} />)}
          </div>
        ) : filteredSubmissions.length === 0 ? (
          <div className="w-full bg-white border border-slate-200/80 rounded-2xl p-12 text-center flex flex-col items-center justify-center space-y-2 shadow-2xs">
            <AlertCircle size={20} className="text-slate-300" />
            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">No matching synchronized records mapped</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            <AnimatePresence mode="popLayout">
              {filteredSubmissions.map((item) => {
                const isEditing = editingId === item.id;
                const isItemProcessing = processingId === item.id;
                
                return (
                  <motion.div
                    key={item.id} layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.98 }} transition={{ duration: 0.15 }}
                    className={`bg-white border border-slate-200/80 rounded-2xl p-5 shadow-xs grid grid-cols-1 lg:grid-cols-12 gap-6 items-start text-left relative transition-all ${
                      isEditing ? 'ring-2 ring-slate-900 border-transparent shadow-md' : ''
                    }`}
                  >
                    
                    {/* Main Content Fields */}
                    <div className="lg:col-span-8 space-y-4 w-full">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 border border-slate-200 text-slate-700 text-[9px] font-bold uppercase rounded">
                          <Binary size={10} /> {isEditing ? "Editing Mode" : (item.schoolCode || "N/A")}
                        </span>
                        
                        {!isEditing && (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase rounded border ${
                            item.status === 'pending' ? 'bg-amber-50 border-amber-200 text-amber-800' :
                            item.status === 'approved' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' :
                            'bg-rose-50 border-rose-200 text-rose-800'
                          }`}>
                            {item.status === 'pending' && <Clock size={10} />}
                            {item.status === 'approved' && <Check size={10} />}
                            {item.status === 'rejected' && <X size={10} />}
                            {item.status}
                          </span>
                        )}
                      </div>

                      <div className="space-y-3">
                        {/* Roster Block */}
                        <div className="flex items-start gap-2.5">
                          <Users2 size={14} className="text-slate-400 shrink-0 mt-1" />
                          <div className="w-full">
                            <h4 className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Identified Members</h4>
                            {isEditing ? (
                              <input type="text" className="w-full mt-1 px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold outline-none focus:border-slate-900 bg-[#fafafa]" value={editFields.student} onChange={e => setEditFields({...editFields, student: e.target.value})} />
                            ) : (
                              <p className="text-sm font-bold text-slate-900 leading-tight">{item.student}</p>
                            )}
                          </div>
                        </div>

                        {/* Title & Organization Info */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                          <div className="flex items-start gap-2.5">
                            <Award size={14} className="text-slate-400 shrink-0 mt-0.5" />
                            <div className="w-full">
                              <h4 className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Achievement Metrics</h4>
                              {isEditing ? (
                                <input type="text" className="w-full mt-1 px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold outline-none focus:border-slate-900 bg-[#fafafa]" value={editFields.achievement} onChange={e => setEditFields({...editFields, achievement: e.target.value})} />
                              ) : (
                                <p className="text-xs font-semibold text-slate-800">{item.achievement}</p>
                              )}
                            </div>
                          </div>

                          <div className="flex items-start gap-2.5">
                            <MapPin size={14} className="text-slate-400 shrink-0 mt-0.5" />
                            <div className="w-full">
                              <h4 className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Host Arena Venue</h4>
                              {isEditing ? (
                                <input type="text" className="w-full mt-1 px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold outline-none focus:border-slate-900 bg-[#fafafa]" value={editFields.destination} onChange={e => setEditFields({...editFields, destination: e.target.value})} />
                              ) : (
                                <p className="text-xs font-semibold text-slate-800">{item.destination}</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Description Context Block */}
                        <div className="flex items-start gap-2.5 pt-1.5 border-t border-slate-50">
                          <FileText size={14} className="text-slate-400 shrink-0 mt-0.5" />
                          <div className="w-full">
                            <h4 className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Structural Context</h4>
                            {isEditing ? (
                              <textarea rows="3" className="w-full mt-1 px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold outline-none focus:border-slate-900 resize-none leading-relaxed bg-[#fafafa]" value={editFields.description} onChange={e => setEditFields({...editFields, description: e.target.value})} />
                            ) : (
                              <p className="text-xs font-medium text-slate-500 leading-relaxed max-w-2xl">{item.description || "No parameter descriptions specified."}</p>
                            )}
                          </div>
                        </div>

                        {/* Student Key Code input (only visible on edit trigger) */}
                        {isEditing && (
                          <div className="flex items-start gap-2.5 pt-1">
                            <Binary size={14} className="text-slate-400 shrink-0 mt-1" />
                            <div className="w-full">
                              <h4 className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Verification Key Code</h4>
                              <input type="text" maxLength="7" className="w-32 mt-1 px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold outline-none focus:border-slate-900 bg-[#fafafa]" value={editFields.schoolCode} onChange={e => setEditFields({...editFields, schoolCode: e.target.value.replace(/\D/g, '')})} />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Middle Column: File Proof Image Canvas Container */}
                    <div className="lg:col-span-2 flex flex-col justify-center h-full">
                      <h4 className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-2 lg:hidden">Verification Context Image</h4>
                      {item.proofImage || item.imageUrl ? (
                        <div 
                          onClick={() => setSelectedImage(item.proofImage || item.imageUrl)}
                          className="relative w-full aspect-4/3 lg:h-20 lg:w-full bg-slate-50 border border-slate-200 rounded-xl overflow-hidden group cursor-pointer hover:border-slate-900 transition-all"
                        >
                          <img src={item.proofImage || item.imageUrl} alt="Document Proof" className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-150" />
                          <div className="absolute inset-0 bg-slate-950/20 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                            <Maximize2 size={12} className="text-white" />
                          </div>
                        </div>
                      ) : (
                        <div className="w-full aspect-4/3 lg:h-20 lg:w-full bg-slate-50 border border-dashed border-slate-200 rounded-xl flex items-center justify-center text-center text-slate-400">
                          <span className="text-[9px] font-bold uppercase tracking-wider">No File Attached</span>
                        </div>
                      )}
                    </div>

                    {/* Right Column: Inline Operational Action Systems */}
                    <div className="lg:col-span-2 lg:h-full flex lg:flex-col justify-end gap-2 pt-4 lg:pt-0 border-t lg:border-t-0 border-slate-100 w-full shrink-0">
                      
                      {isEditing ? (
                        <>
                          <button
                            disabled={isItemProcessing} 
                            onClick={() => handleSaveInlineEdit(item.id)}
                            className="w-full h-[34px] bg-slate-900 disabled:bg-slate-700 text-white font-bold text-[10px] uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                          >
                            {isItemProcessing ? (
                              <svg className="animate-spin h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                            ) : (
                              <>
                                <Save size={12} /> Save
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="w-full h-[34px] bg-white border border-slate-200 text-slate-600 hover:text-slate-900 font-bold text-[10px] uppercase tracking-wider rounded-xl transition-all flex items-center justify-center cursor-pointer"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          {/* Config Controls Button Grid */}
                          <div className="flex gap-1 w-full">
                            <button
                              disabled={isItemProcessing} onClick={() => startEditing(item)}
                              className="flex-1 h-[34px] bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 font-bold text-[10px] uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-1 cursor-pointer"
                            >
                              <Edit2 size={11} /> Edit
                            </button>
                            <button
                              disabled={isItemProcessing} onClick={() => handleDeleteRecord(item.id)}
                              className="h-[34px] w-[34px] bg-white hover:bg-rose-50 text-slate-400 hover:text-rose-600 border border-slate-200 hover:border-rose-200 rounded-xl transition-all flex items-center justify-center cursor-pointer shrink-0"
                            >
                              {isItemProcessing ? (
                                <svg className="animate-spin h-3 w-3 text-slate-500" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                              ) : (
                                <Trash2 size={12} />
                              )}
                            </button>
                          </div>

                          {/* Instant State Modifier Routers */}
                          <div className="grid grid-cols-2 gap-1 w-full">
                            {item.status !== 'approved' && (
                              <button
                                disabled={isItemProcessing} onClick={() => handleUpdateStatus(item.id, 'approved')}
                                className="h-[30px] bg-emerald-50 border border-emerald-200/50 text-emerald-800 font-bold text-[9px] uppercase tracking-wider rounded-lg hover:bg-emerald-100 transition-all cursor-pointer flex items-center justify-center"
                              >
                                Approve
                              </button>
                            )}
                            {item.status !== 'rejected' && (
                              <button
                                disabled={isItemProcessing} onClick={() => handleUpdateStatus(item.id, 'rejected')}
                                className="h-[30px] bg-rose-50 border border-rose-200/50 text-rose-800 font-bold text-[9px] uppercase tracking-wider rounded-lg hover:bg-rose-100 transition-all cursor-pointer flex items-center justify-center"
                              >
                                Reject
                              </button>
                            )}
                            {item.status !== 'pending' && (
                              <button
                                disabled={isItemProcessing} onClick={() => handleUpdateStatus(item.id, 'pending')}
                                className="col-span-2 h-[30px] bg-amber-50 border border-amber-200/50 text-amber-800 font-bold text-[9px] uppercase tracking-wider rounded-lg hover:bg-amber-100 transition-all cursor-pointer flex items-center justify-center"
                              >
                                Reset To Pending
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>

                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* --- Lightbox Overlay Viewport Modal --- */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSelectedImage(null)}
            className="fixed inset-0 bg-slate-950/80 z-50 flex items-center justify-center p-4 backdrop-blur-xs cursor-zoom-out"
          >
            <motion.div
              initial={{ scale: 0.97, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.97, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 380 }} onClick={(e) => e.stopPropagation()}
              className="relative max-w-4xl max-h-[85vh] bg-white rounded-2xl overflow-hidden shadow-2xl p-2 border border-slate-800"
            >
              <img src={selectedImage} alt="Verification Context Detail" className="max-w-full max-h-[80vh] rounded-xl object-contain bg-slate-50" />
              <button
                type="button" onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 p-2 bg-slate-950/60 hover:bg-slate-950 text-white rounded-full transition-colors cursor-pointer"
              >
                <X size={14} />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminDashboard;